import { useCallback } from "react";
import { useSettings } from "../contexts/SettingsContext";
import {
  mergeDomainLists,
  notifyBlockerStateUpdated,
  resolveBlockerTarget,
  type BlockerHelperStatus,
  type BlockerStatus,
} from "../lib/platform-blocker";

interface BlockerApi {
  enable: () => Promise<unknown>;
  enableFast: () => Promise<unknown>;
  disable: () => Promise<unknown>;
  disableFast?: () => Promise<unknown>;
  getStatus?: () => Promise<BlockerStatus>;
  getHelperStatus?: () => Promise<BlockerHelperStatus>;
  installHelper?: () => Promise<BlockerHelperStatus>;
  setDomains: (
    domains: string[],
  ) => Promise<{ ok: boolean; domains?: string[]; reason?: string }>;
}

interface LockoutElectronApi {
  checkAccessibility?: () => Promise<{ granted: boolean }>;
  requestAccessibility?: () => Promise<{ granted: boolean }>;
}

export function useLockoutBlocker() {
  const {
    defaultPlatform,
    proposerIframeSources,
    blockerQuickTarget,
    blockerCustomDomains,
    lockoutPermission,
    setLockoutPermission,
  } = useSettings();

  const ensureSelectedPlatformBlocked = useCallback(
    async ({ preferSystemLayer }: { preferSystemLayer: boolean }) => {
      const api = getBlockerApi();
      if (!api) return;
      const target = resolveBlockerTarget({
        target: blockerQuickTarget,
        sources: proposerIframeSources,
        selectedPlatform: defaultPlatform,
      });
      const domains = mergeDomainLists(
        target?.domains ?? [],
        blockerCustomDomains,
      );
      if (domains.length === 0) return;

      try {
        const result = await api.setDomains(domains);
        if (!result.ok) return;
        const status = api.getStatus ? await api.getStatus() : null;
        const hasSystemLayer =
          !!status?.layers?.hosts || !!status?.layers?.resolver;
        if (preferSystemLayer || hasSystemLayer) await api.enable();
        await api.enableFast();
        notifyBlockerStateUpdated();
      } catch {
        // Server state remains authoritative; the UI surfaces the next refresh.
      }
    },
    [
      blockerCustomDomains,
      blockerQuickTarget,
      defaultPlatform,
      proposerIframeSources,
    ],
  );

  const releaseSelectedPlatformBlocker = useCallback(async () => {
    const api = getBlockerApi();
    if (!api) return;
    try {
      const status = api.getStatus ? await api.getStatus() : null;
      const hasSystemLayer =
        !!status?.layers?.hosts || !!status?.layers?.resolver;
      if (hasSystemLayer) await api.disable();
      if (api.disableFast) await api.disableFast();
      else if (!hasSystemLayer) await api.disable();
      notifyBlockerStateUpdated();
    } catch {
      // A later status refresh retries the safe release path.
    }
  }, []);

  const hasSystemBlockerGate = useCallback(async () => {
    const api = getBlockerApi();
    if (!api?.getHelperStatus) return false;
    try {
      const status = await api.getHelperStatus();
      if (status.running && lockoutPermission !== "granted")
        setLockoutPermission("granted");
      return !!status.running;
    } catch {
      return false;
    }
  }, [lockoutPermission, setLockoutPermission]);

  const requestSystemPermission = useCallback(async () => {
    const blockerApi = getBlockerApi();
    if (blockerApi?.installHelper) {
      try {
        const helper = await blockerApi.installHelper();
        if (helper.running) {
          setLockoutPermission("granted");
          return true;
        }
      } catch {
        // Accessibility fallback remains available below.
      }
    }

    const api = getLockoutApi();
    if (!api?.checkAccessibility) return false;
    try {
      const current = await api.checkAccessibility();
      if (current.granted) {
        setLockoutPermission("prompt");
        return false;
      }
      const requested = api.requestAccessibility
        ? await api.requestAccessibility()
        : current;
      setLockoutPermission(requested.granted ? "prompt" : "denied");
      return false;
    } catch {
      return false;
    }
  }, [setLockoutPermission]);

  return {
    ensureSelectedPlatformBlocked,
    hasSystemBlockerGate,
    releaseSelectedPlatformBlocker,
    requestSystemPermission,
  };
}

function getBlockerApi(): BlockerApi | null {
  const runtime = window as unknown as { electron?: { blocker?: BlockerApi } };
  return runtime.electron?.blocker ?? null;
}

function getLockoutApi(): LockoutElectronApi | null {
  const runtime = window as unknown as {
    electron?: { lockout?: LockoutElectronApi };
  };
  return runtime.electron?.lockout ?? null;
}
